import React, { useContext } from 'react'
import { Table } from 'semantic-ui-react'
import { ThemeContext } from 'styled-components'
import InfoPopup from '~/components/InfoPopup'
import { formatNumber } from '~/utils/helpers'
import { StyledResultsFooter, StyledTableCell } from './style'
import { CowResultsType, CattleResultsType } from '~/api/services/climaCheck'
import { Nullable } from '~/types'

const cowTableColumHeaders = [
  {
    name: `<div>
      <div>THG-Emissionen gesamt</div>
      <div style="white-space: nowrap;">Milchkuhhaltung</div>
    </div>`,
    key: 'total',
    unit: 'kg CO2äq./Kuh und Jahr',
    type: 'no-unit-number',
    total: true
  },
  {
    name: 'Bestands­ergänzung',
    key: 'inventoryAddition',
    unit: '%'
  },
  {
    name: 'Kälberaufzucht',
    key: 'calfRearing',
    unit: '%'
  },
  {
    name: 'Verdauung',
    key: 'digestion',
    unit: '%'
  },
  {
    name: 'Futtermittelherstellung- und verluste',
    key: 'feedProductionAndLosses',
    unit: '%'
  },
  {
    name: 'Wirtschaftsdünger und Einstreu',
    key: 'waterAndEnergy',
    unit: '%'
  },
  {
    name: 'Dieselherstellung- und verbrennung',
    key: 'dieselProductionAndCombustion',
    unit: '%'
  },
  {
    name: 'Maschinenherstellung',
    key: 'machineManufacturing',
    unit: '%'
  }
]

const heiferTableColumnHeaders = [
  {
    name: `<div>
      <div>THG-Emissionen gesamt</div>
      <div>Färsenaufzucht</div>
    </div>`,
    key: 'youngCattleTotal',
    unit: 'kg CO₂-Äq./erz. Färse',
    type: 'no-unit-number',
    total: true
  },
  {
    name: 'weibliches Kalb',
    key: 'youngCattleFemaleCalf',
    unit: '%'
  },
  {
    name: 'Verdauung',
    key: 'youngCattleDigestion',
    unit: '%'
  },
  {
    name: 'Futtermittelherstellung- und verluste',
    key: 'youngCattleFeedProductionAndLosses',
    unit: '%'
  },
  {
    name: 'Wirtschaftsdünger und Einstreu',
    key: 'youngCattleManureAndBedding',
    unit: '%'
  },
  {
    name: 'Wasser- und Energieeinsatz',
    key: 'youngCattleWaterAndEnergy',
    unit: '%'
  },
  {
    name: 'Dieselherstellung- und verbrennung',
    key: 'youngCattleDieselProductionAndCombustion',
    unit: '%'
  },
  {
    name: 'Maschinenherstellung',
    key: 'youngCattleMachineManufacturing',
    unit: '%'
  }
]

const resultColumnHeaders = [
  {
    name: `<div>
      <div>Produkt</div>
      <div style="white-space: nowrap;">CO₂-Fußabdruck</div>
    </div>`,
    total: true
  },
  {
    name: '*Milch',
    key: 'ghgEmissionMilkIdf',
    unit: 'kg CO₂-Äq./kg Milch (FPCM)'
  },
  {
    name: 'Schlachtkuh pro kg Lebendgewicht',
    key: 'ghgEmissionSlaughterCowsIdf',
    unit: 'kg CO₂-Äq./kg LG Schlachtkuh'
  },
  {
    name: 'Kalb je kg Lebendgewicht (28. Lebenstag)',
    key: 'ghgEmissionCalvesIdf',
    unit: 'kg CO₂-Äq./je kg LG Kalb'
  },
  {
    name: 'erzeugte Färse',
    key: 'youngCattleGhgEmissionHeifersIdf',
    unit: 'kg CO₂-Äq./je erz. Färse'
  }
]

interface Props {
  results: {
    cowResults: CowResultsType
    heiferResults: Nullable<CattleResultsType>
  }
  removeMargin?: boolean
}

const Results: React.FC<Props> = ({ results, removeMargin }) => {
  const { firstColor } = useContext(ThemeContext)

  function renderCellValue(value, row) {
    switch (row.type) {
      case 'no-unit-number':
        return value ? formatNumber(value, 0) : '0,00'
      default:
        return formatNumber(value, 2) || ''
    }
  }

  function getStyleColumn() {
    return {
      background: firstColor,
      color: 'white'
    }
  }

  function renderResults(results, tableColumnHeaders) {
    return <Table style={{ marginTop: removeMargin ? 0 : 80, marginBottom: 15 }} celled compact>

      <Table.Body>
        {tableColumnHeaders.map(showDataLabelAndKey => {
          const isTotalColumn = Boolean(showDataLabelAndKey?.total)
          return <Table.Row key={showDataLabelAndKey.key}>
            <StyledTableCell
              style={isTotalColumn ? getStyleColumn() : {}}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div
                  className={showDataLabelAndKey?.className || ''}
                  dangerouslySetInnerHTML={{ __html: `${showDataLabelAndKey.name}` }} />
                {showDataLabelAndKey?.info && <InfoPopup
                  content={showDataLabelAndKey.info}
                  wide='very'
                  miWidth={600}
                  styleIcon={isTotalColumn ? { ...getStyleColumn(), fontSize: '1.3em' } : { fontSize: '1.3em' }}
                  position={'top center'}
                />}
              </div>
            </StyledTableCell>
            <StyledTableCell
              style={isTotalColumn ? getStyleColumn() : {}}
            >
              {showDataLabelAndKey.key && <div style={{ whiteSpace: 'nowrap' }} className={showDataLabelAndKey?.className || ''}>
                {`${renderCellValue(results[showDataLabelAndKey.key], showDataLabelAndKey)} ${showDataLabelAndKey.unit}`}
              </div>}
            </StyledTableCell>
          </Table.Row>
        })}
      </Table.Body>
    </Table>
  }

  if(!results.cowResults) {
    return <></>
  }

  const totalResults = {
    ghgEmissionMilkIdf: results?.cowResults?.ghgEmissionMilkIdf,
    ghgEmissionSlaughterCowsIdf: results?.cowResults?.ghgEmissionSlaughterCowsIdf,
    ghgEmissionCalvesIdf: results?.cowResults?.ghgEmissionCalvesIdf,
    youngCattleGhgEmissionHeifersIdf: results?.heiferResults?.youngCattleGhgEmissionHeifersIdf || ''
  }

  return <>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <div>
        {renderResults(totalResults, resultColumnHeaders)}
        <StyledResultsFooter>
          <div dangerouslySetInnerHTML={{ __html: '*Die normale Spanne für den Wert nach IDF liegt etwa zwischen 0,8 und 2,0 kg CO<sub>2</sub>-Äquivalente/kg Milch' }} />
        </StyledResultsFooter>
      </div>
      {renderResults(results?.cowResults, cowTableColumHeaders)}
      {results?.heiferResults && renderResults(results?.heiferResults, heiferTableColumnHeaders)}
    </div>
  </>
}
export default Results